export interface LiaisonValidationLike {
  passed: boolean;
  issues: string[];
}

export interface AuditCriterionLike {
  reasoning?: string;
}

export interface AuditResultLike {
  verdict?: string;
  overallScore?: number;
  criteria?: AuditCriterionLike[];
  blockingIssues?: string[];
  warnings?: string[];
}

export interface ImplAuditLike {
  data?: {
    auditResult?: AuditResultLike;
  } | null;
}

export interface PhaseRetryDecision {
  shouldRetry: boolean;
  strategy: 'none' | 'liaison-retry' | 'fix' | 'regenerate';
  reason: string;
}

const TRANSIENT_PATTERNS = [
  /\btimeout\b/i,
  /\btemporar(?:y|ily)\b/i,
  /\bnetwork\b/i,
  /\bconnection\b/i,
  /\bstream(?:ing)? error\b/i,
  /\beconn|enotfound|socket hang up\b/i,
  /\b429\b|\b503\b|\b504\b/i,
];

const DETERMINISTIC_PATTERNS = [
  /interaction prompt validation failed/i,
  /interaction dependency validation failed/i,
  /hardcoded references do not match prior artifacts/i,
  /prior visual artifacts contain 0/i,
  /no visual-editor component ids are available/i,
  /depends on prior artifacts/i,
  /upstream/i,
  /not yet available/i,
];

function gatherSignals(
  executeError: string | undefined,
  liaisonValidation: LiaisonValidationLike | null | undefined,
  implAudit: ImplAuditLike | null | undefined,
): string[] {
  const signals: string[] = [];
  if (executeError) signals.push(executeError);
  if (liaisonValidation?.issues?.length) signals.push(...liaisonValidation.issues);

  const auditResult = implAudit?.data?.auditResult;
  if (auditResult?.blockingIssues?.length) signals.push(...auditResult.blockingIssues);
  if (auditResult?.warnings?.length) signals.push(...auditResult.warnings);
  if (auditResult?.criteria?.length) {
    for (const criterion of auditResult.criteria) {
      if (criterion.reasoning) signals.push(criterion.reasoning);
    }
  }

  return signals;
}

function hasMatchingSignal(signals: string[], patterns: RegExp[]): boolean {
  return signals.some((signal) => patterns.some((pattern) => pattern.test(signal)));
}

export function decidePhaseRetry(params: {
  domain: string;
  hadStreamingErrors: boolean;
  executeError?: string;
  liaisonValidation?: LiaisonValidationLike | null;
  implAudit?: ImplAuditLike | null;
  catastrophicThreshold?: number;
}): PhaseRetryDecision {
  const {
    domain,
    hadStreamingErrors,
    executeError,
    liaisonValidation,
    implAudit,
    catastrophicThreshold = 30,
  } = params;

  const isLiaisonPhase = domain === 'data' || domain === 'workflow';
  const auditResult = implAudit?.data?.auditResult;
  const failed = isLiaisonPhase
    ? (liaisonValidation !== null && liaisonValidation !== undefined && !liaisonValidation.passed)
    : auditResult?.verdict === 'fail';

  if (!failed) {
    return { shouldRetry: false, strategy: 'none', reason: 'phase passed' };
  }

  const signals = gatherSignals(executeError, liaisonValidation, implAudit);
  const transientFailure = hadStreamingErrors || hasMatchingSignal(signals, TRANSIENT_PATTERNS);
  const deterministicFailure = hasMatchingSignal(signals, DETERMINISTIC_PATTERNS);

  if (deterministicFailure) {
    return {
      shouldRetry: false,
      strategy: 'none',
      reason: 'failure is deterministic and will not improve with the same phase retry',
    };
  }

  if (isLiaisonPhase) {
    if (transientFailure) {
      return {
        shouldRetry: true,
        strategy: 'liaison-retry',
        reason: 'liaison phase shows transient failure signals',
      };
    }
    return {
      shouldRetry: false,
      strategy: 'none',
      reason: 'liaison phase failed without transient signals; same-prompt retry is low-value',
    };
  }

  const auditScore = auditResult?.overallScore ?? 0;
  return {
    shouldRetry: true,
    strategy: auditScore < catastrophicThreshold ? 'regenerate' : 'fix',
    reason: transientFailure
      ? 'phase failed with transient signals; retry is warranted'
      : 'phase failed semantically and may improve with prompt correction',
  };
}
